import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  ApiDepot,
  ApiRole,
  ApiUser,
  assignUserDepot,
  assignUserRole,
  getUsersData,
  setUserActive,
} from "../api";
import { getErrorMessage } from "../../../shared/api/http";
import { colors } from "../../../shared/ui/tokens";

// ─────────────────────────────────────────────────────────────────────────────
// src/pages/UsersPage.tsx
// ─────────────────────────────────────────────────────────────────────────────
// Page unique "Users" (remplace l'onglet "Attribution Role" dans l'AppBar).
// Consomme le microservice unique UsersController.cs :
//
//   GET  /api/users/data            → { users, roles, depots }
//   POST /api/users/assign-role     → { userId, roleId }
//   PUT  /api/users/{id}/depot      → { codDepot }
//   PUT  /api/users/{id}/activate
//   PUT  /api/users/{id}/deactivate
//
// Visible uniquement pour l'Admin Général (cf. AppBar.tsx → isAdminGeneral).
// Les rôles "Responsable Boutique" et "Vendeur" n'ont pas accès à cette page
// (l'onglet est simplement absent de leur AppBar — rien d'autre à changer).
// ─────────────────────────────────────────────────────────────────────────────

// ── AVDASH palette ────────────────────────────────────────────────────────────
const NW = {
  bgDeep: colors.brand[600],
  bgMid: colors.brand[500],
  bgLight: colors.brand[500],
  mauve: colors.accent[300],
  mauveDim: "rgba(127,200,189,0.15)",
  mauveBorder: "rgba(127,200,189,0.25)",
  success: colors.accent[400],
  successBg: "rgba(77,179,165,0.10)",
  error: colors.danger[500],
  errorBg: "rgba(214,69,69,0.10)",
  textMuted: colors.neutral[500],
  border: colors.brand[100],
};

// ── Types ─────────────────────────────────────────────────────────────────
interface DepotOption {
  codDepot: string;
  designation: string;
}

interface NormalizedUser {
  userId: string;
  username: string;
  fullName: string;
  email: string;
  phone: string;
  roleId: string;
  roleLabel: string;
  isActive: boolean;
  photoUrl: string;
  depot: string;
}

interface RoleOption {
  id: string;
  label: string;
}

// ── Helpers de normalisation (le backend renvoie des clés non uniformes) ────
const normalizeUser = (u: ApiUser): NormalizedUser => ({
  userId: String(u.userId ?? u.id ?? ""),
  username: u.username ?? "",
  fullName: u.fullName ?? u.username ?? "Utilisateur",
  email: u.email ?? "",
  phone: u.phone ?? "",
  roleId: String(u.idRole ?? ""),
  roleLabel: u.nom?.trim() ? u.nom.trim() : "Sans rôle",
  isActive: u.isActive === 1 || u.isActive === true,
  photoUrl: u.photoUrl ?? "",
  depot: u.depot ?? "",
});

const normalizeRole = (r: ApiRole): RoleOption => ({
  id: String(r.id ?? r.idRole ?? ""),
  label: r.nom ?? "Rôle",
});

const normalizeDepot = (d: ApiDepot): DepotOption => ({
  codDepot: d.cod_dep ?? "",
  designation: d.adr_dep || d.cod_dep || "",
});

// ── API ──────────────────────────────────────────────────────────────────────
async function apiGetData(): Promise<{
  users: NormalizedUser[];
  roles: RoleOption[];
  depots: DepotOption[];
}> {
  const json = await getUsersData();
  if (!json.success) throw new Error(json.message || "Erreur de chargement");
  return {
    users: (json.users ?? []).map(normalizeUser),
    roles: (json.roles ?? []).map(normalizeRole),
    depots: (json.depots ?? []).map(normalizeDepot),
  };
}

// ── Toast minimal (succès / erreur) ──────────────────────────────────────────
interface ToastState {
  type: "success" | "error";
  message: string;
}

const Toast: React.FC<{ toast: ToastState | null }> = ({ toast }) => {
  if (!toast) return null;
  const isSuccess = toast.type === "success";
  return (
    <div
      style={{
        position: "fixed",
        bottom: 24,
        right: 24,
        background: isSuccess ? "#00A08C" : "#D64545",
        color: "white",
        padding: "12px 18px",
        borderRadius: 10,
        fontSize: 13,
        fontWeight: 600,
        boxShadow: "0 8px 24px rgba(15,23,42,0.18)",
        zIndex: 1000,
        animation: "nwToastIn 0.2s ease-out",
      }}
    >
      {toast.message}
    </div>
  );
};

// ── Avatar avec initiale ─────────────────────────────────────────────────────
const Avatar: React.FC<{ name: string; photoUrl?: string; size?: number }> = ({
  name,
  photoUrl,
  size = 44,
}) => {
  const initial = name.trim().charAt(0).toUpperCase() || "U";
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        background: NW.mauveDim,
        color: NW.bgMid,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: 800,
        fontSize: size * 0.4,
        flexShrink: 0,
        overflow: "hidden",
      }}
    >
      {photoUrl ? (
        <img
          src={photoUrl}
          alt={name}
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      ) : (
        initial
      )}
    </div>
  );
};

// ── Switch actif/inactif (même comportement que le toggle Flutter) ─────────
const StatusSwitch: React.FC<{
  checked: boolean;
  loading: boolean;
  onToggle: () => void;
}> = ({ checked, loading, onToggle }) => {
  if (loading) {
    return (
      <div
        style={{
          width: 18,
          height: 18,
          borderRadius: "50%",
          border: `2px solid ${NW.mauveBorder}`,
          borderTopColor: NW.bgMid,
          animation: "nwSpin 0.7s linear infinite",
        }}
      />
    );
  }
  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        onToggle();
      }}
      aria-label={
        checked ? "Désactiver l'utilisateur" : "Activer l'utilisateur"
      }
      style={{
        width: 40,
        height: 22,
        borderRadius: 12,
        border: "none",
        cursor: "pointer",
        padding: 2,
        background: checked ? "#4DB3A5" : "#CBD5E1",
        display: "flex",
        alignItems: "center",
        justifyContent: checked ? "flex-end" : "flex-start",
        transition: "background 0.2s",
      }}
    >
      <span
        style={{
          width: 18,
          height: 18,
          borderRadius: "50%",
          background: "white",
          boxShadow: "0 1px 3px rgba(15,23,42,0.25)",
          transition: "transform 0.2s",
        }}
      />
    </button>
  );
};

// ── Modal détail utilisateur : rôle + dépôt ──────────────────────────────────
interface UserModalProps {
  user: NormalizedUser;
  roles: RoleOption[];
  depots: DepotOption[];
  onClose: () => void;
  onSaved: (updated: Partial<NormalizedUser>) => void;
  notify: (t: ToastState) => void;
}

const UserModal: React.FC<UserModalProps> = ({
  user,
  roles,
  depots,
  onClose,
  onSaved,
  notify,
}) => {
  const [roleId, setRoleId] = useState(user.roleId);
  const [codDepot, setCodDepot] = useState(user.depot);
  const [savingRole, setSavingRole] = useState(false);
  const [savingDepot, setSavingDepot] = useState(false);

  const currentRoleLabel = user.roleLabel;
  const depotLabel = (code: string) => code || "Aucun dépôt";

  const roleChanged = roleId !== user.roleId && roleId !== "";
  const depotChanged = codDepot !== user.depot && codDepot !== "";

  const handleSaveRole = async () => {
    if (!roleChanged) return;
    setSavingRole(true);
    try {
      const res = await assignUserRole(user.userId, roleId);
      if (res.success) {
        const label = roles.find((r) => r.id === roleId)?.label ?? "";
        onSaved({ roleId, roleLabel: label });
        notify({ type: "success", message: "Rôle mis à jour." });
      } else {
        notify({
          type: "error",
          message: res.message || "Échec de la mise à jour du rôle.",
        });
      }
    } catch (e) {
      notify({
        type: "error",
        message: getErrorMessage(e, "Échec de la mise à jour du rôle."),
      });
    } finally {
      setSavingRole(false);
    }
  };

  const handleSaveDepot = async () => {
    if (!depotChanged) return;
    setSavingDepot(true);
    try {
      const res = await assignUserDepot(user.userId, codDepot);
      if (res.success) {
        onSaved({ depot: codDepot });
        notify({
          type: "success",
          message: `Dépôt mis à jour : ${depotLabel(codDepot)}`,
        });
      } else {
        notify({
          type: "error",
          message: res.message || "Échec de la mise à jour du dépôt.",
        });
      }
    } catch (e) {
      notify({
        type: "error",
        message: getErrorMessage(e, "Échec de la mise à jour du dépôt."),
      });
    } finally {
      setSavingDepot(false);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(13,42,80,0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 500,
        animation: "nwFadeIn 0.15s ease-out",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 420,
          maxWidth: "90vw",
          background: "white",
          borderRadius: 16,
          overflow: "hidden",
          boxShadow: "0 20px 60px rgba(15,23,42,0.3)",
        }}
      >
        {/* Header */}
        <div
          style={{
            background: NW.mauveDim,
            padding: "20px 22px",
            display: "flex",
            alignItems: "flex-start",
            gap: 14,
            position: "relative",
          }}
        >
          <Avatar name={user.fullName} photoUrl={user.photoUrl} size={52} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 800, fontSize: 16, color: "#0D2A50" }}>
              {user.fullName}
            </div>
            <div style={{ fontSize: 12.5, color: NW.textMuted }}>
              @{user.username}
            </div>
            {user.email && (
              <div style={{ fontSize: 12.5, color: NW.textMuted }}>
                {user.email}
              </div>
            )}
            <div
              style={{
                fontSize: 12.5,
                color: NW.bgMid,
                marginTop: 4,
                fontWeight: 600,
              }}
            >
              🏬 Dépôt : {depotLabel(user.depot)}
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              border: "none",
              background: "transparent",
              fontSize: 18,
              cursor: "pointer",
              color: NW.textMuted,
              lineHeight: 1,
            }}
            aria-label="Fermer"
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div
          style={{
            padding: "20px 22px",
            display: "flex",
            flexDirection: "column",
            gap: 20,
          }}
        >
          {/* ── Rôle ── */}
          <div>
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: NW.textMuted,
                letterSpacing: 0.6,
                marginBottom: 8,
              }}
            >
              RÔLE ACTUEL
            </div>
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "6px 12px",
                borderRadius: 20,
                background: NW.mauveDim,
                border: `1px solid ${NW.mauveBorder}`,
                color: NW.bgMid,
                fontWeight: 700,
                fontSize: 13,
                marginBottom: 14,
              }}
            >
              🏷️ {currentRoleLabel}
            </div>

            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: NW.textMuted,
                letterSpacing: 0.6,
                marginBottom: 8,
              }}
            >
              NOUVEAU RÔLE
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <select
                value={roleId}
                onChange={(e) => setRoleId(e.target.value)}
                style={selectStyle}
              >
                {roles
                  .filter((r) =>
                    ["R0000001", "R0000004", "R0000006"].includes(r.id),
                  )
                  .map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.label}
                    </option>
                  ))}
              </select>
              <button
                disabled={!roleChanged || savingRole}
                onClick={handleSaveRole}
                style={actionBtnStyle(roleChanged, savingRole)}
              >
                {savingRole ? "…" : "✓"}
              </button>
            </div>
          </div>

          <div style={{ height: 1, background: NW.border }} />

          {/* ── Dépôt ── */}
          <div>
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: NW.textMuted,
                letterSpacing: 0.6,
                marginBottom: 8,
              }}
            >
              DÉPÔT
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <select
                value={codDepot}
                onChange={(e) => setCodDepot(e.target.value)}
                style={selectStyle}
              >
                <option value="">— Sélectionner un dépôt —</option>
                {depots.map((d) => (
                  <option key={d.codDepot} value={d.codDepot}>
                    {d.codDepot}
                  </option>
                ))}
              </select>
              <button
                disabled={!depotChanged || savingDepot}
                onClick={handleSaveDepot}
                style={actionBtnStyle(depotChanged, savingDepot)}
              >
                {savingDepot ? "…" : "✓"}
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div
          style={{
            padding: "14px 22px",
            display: "flex",
            justifyContent: "flex-end",
            borderTop: `1px solid ${NW.border}`,
          }}
        >
          <button
            onClick={onClose}
            style={{
              padding: "9px 18px",
              borderRadius: 8,
              border: "none",
              background: "#F1F5F9",
              color: "#334155",
              fontWeight: 600,
              fontSize: 13,
              cursor: "pointer",
            }}
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
};

const selectStyle: React.CSSProperties = {
  flex: 1,
  padding: "9px 12px",
  borderRadius: 8,
  border: `1px solid ${NW.border}`,
  fontSize: 13,
  color: "#0D2A50",
  background: "white",
  cursor: "pointer",
};

const actionBtnStyle = (
  enabled: boolean,
  loading: boolean,
): React.CSSProperties => ({
  width: 38,
  borderRadius: 8,
  border: "none",
  cursor: enabled && !loading ? "pointer" : "not-allowed",
  background: enabled ? NW.bgMid : "#E2E8F0",
  color: enabled ? "white" : "#64748B",
  fontWeight: 700,
  fontSize: 14,
});

// ── Carte utilisateur (liste) ────────────────────────────────────────────────
interface UserRowProps {
  user: NormalizedUser;
  togglingId: string | null;
  onOpen: () => void;
  onToggleStatus: () => void;
}

const UserRow: React.FC<UserRowProps> = ({
  user,
  togglingId,
  onOpen,
  onToggleStatus,
}) => {
  const isToggling = togglingId === user.userId;
  return (
    <div
      onClick={onOpen}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 14,
        padding: "12px 16px",
        background: "white",
        borderRadius: 12,
        marginBottom: 10,
        boxShadow: "0 1px 4px rgba(15,23,42,0.06)",
        cursor: "pointer",
        transition: "box-shadow 0.15s",
      }}
      onMouseEnter={(e) =>
        (e.currentTarget.style.boxShadow = "0 2px 10px rgba(15,23,42,0.10)")
      }
      onMouseLeave={(e) =>
        (e.currentTarget.style.boxShadow = "0 1px 4px rgba(15,23,42,0.06)")
      }
    >
      <Avatar name={user.fullName} photoUrl={user.photoUrl} />

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 700, fontSize: 14.5, color: "#0D2A50" }}>
          {user.fullName}
        </div>
        <div style={{ fontSize: 12.5, color: NW.textMuted }}>
          {user.roleLabel}
        </div>
        {user.depot && (
          <div
            style={{
              fontSize: 11.5,
              color: NW.bgMid,
              marginTop: 2,
              fontWeight: 600,
            }}
          >
            🏬 {user.depot}
          </div>
        )}
      </div>

      <span
        style={{
          fontSize: 11,
          fontWeight: 700,
          padding: "3px 9px",
          borderRadius: 20,
          background: user.isActive ? NW.successBg : NW.errorBg,
          color: user.isActive ? "#00A08C" : "#D64545",
        }}
      >
        {user.isActive ? "Actif" : "Inactif"}
      </span>

      <StatusSwitch
        checked={user.isActive}
        loading={isToggling}
        onToggle={onToggleStatus}
      />

      <span style={{ color: "#CBD5E1", fontSize: 18 }}>›</span>
    </div>
  );
};

// ── Barre de recherche ───────────────────────────────────────────────────────
const SearchBar: React.FC<{ value: string; onChange: (v: string) => void }> = ({
  value,
  onChange,
}) => (
  <div style={{ marginBottom: 14 }}>
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder="Rechercher (min. 3 caractères)…"
      style={{
        width: "100%",
        padding: "10px 14px",
        borderRadius: 10,
        border: `1px solid ${NW.border}`,
        fontSize: 13.5,
        outline: "none",
        boxSizing: "border-box",
      }}
    />
  </div>
);

// ── Page principale ──────────────────────────────────────────────────────────
const UsersPage: React.FC = () => {
  const [users, setUsers] = useState<NormalizedUser[]>([]);
  const [roles, setRoles] = useState<RoleOption[]>([]);
  const [depots, setDepots] = useState<DepotOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [selectedUser, setSelectedUser] = useState<NormalizedUser | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [toast, setToast] = useState<ToastState | null>(null);

  const notify = useCallback((t: ToastState) => {
    setToast(t);
    setTimeout(() => setToast(null), 2500);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await apiGetData();
      setUsers(data.users);
      setRoles(data.roles);
      setDepots(
        data.depots.map((d) => ({
          ...d,
          designation: d.designation || d.codDepot,
        })),
      );
    } catch (e) {
      setError(getErrorMessage(e, "Erreur de chargement."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (q.length < 3) return users;
    return users.filter(
      (u) =>
        u.fullName.toLowerCase().includes(q) ||
        u.username.toLowerCase().includes(q) ||
        u.roleLabel.toLowerCase().includes(q) ||
        u.email.toLowerCase().includes(q) ||
        u.depot.toLowerCase().includes(q),
    );
  }, [users, search]);

  const handleToggleStatus = async (user: NormalizedUser) => {
    setTogglingId(user.userId);
    try {
      const res = await setUserActive(user.userId, !user.isActive);
      if (res.success) {
        setUsers((prev) =>
          prev.map((u) =>
            u.userId === user.userId ? { ...u, isActive: !u.isActive } : u,
          ),
        );
        notify({
          type: "success",
          message: !user.isActive
            ? "Utilisateur activé."
            : "Utilisateur désactivé.",
        });
      } else {
        notify({ type: "error", message: res.message || "Opération échouée." });
      }
    } catch (e) {
      notify({ type: "error", message: getErrorMessage(e, "Opération échouée.") });
    } finally {
      setTogglingId(null);
    }
  };

  const handleSavedFromModal = (updated: Partial<NormalizedUser>) => {
    if (!selectedUser) return;
    setUsers((prev) =>
      prev.map((u) =>
        u.userId === selectedUser.userId ? { ...u, ...updated } : u,
      ),
    );
    setSelectedUser((prev) => (prev ? { ...prev, ...updated } : prev));
  };

  return (
    <div style={{ padding: "24px 28px", maxWidth: 760, margin: "0 auto" }}>
      <div style={{ marginBottom: 18 }}>
        <h1
          style={{ fontSize: 20, fontWeight: 800, color: "#0D2A50", margin: 0 }}
        >
          Utilisateurs
        </h1>
        <p style={{ fontSize: 13, color: NW.textMuted, marginTop: 4 }}>
          Gérer les rôles, dépôts et l'accès des utilisateurs.
        </p>
      </div>

      <SearchBar value={search} onChange={setSearch} />

      {loading && (
        <div style={{ textAlign: "center", padding: 40, color: NW.textMuted }}>
          Chargement…
        </div>
      )}

      {!loading && error && (
        <div
          style={{
            textAlign: "center",
            padding: 40,
            color: "#D64545",
            background: NW.errorBg,
            borderRadius: 12,
          }}
        >
          {error}
          <div style={{ marginTop: 12 }}>
            <button
              onClick={load}
              style={{
                padding: "8px 16px",
                borderRadius: 8,
                border: "none",
                background: NW.bgMid,
                color: "white",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Réessayer
            </button>
          </div>
        </div>
      )}

      {!loading && !error && filteredUsers.length === 0 && (
        <div style={{ textAlign: "center", padding: 40, color: NW.textMuted }}>
          Aucun utilisateur trouvé.
        </div>
      )}

      {!loading &&
        !error &&
        filteredUsers.map((u) => (
          <UserRow
            key={u.userId}
            user={u}
            togglingId={togglingId}
            onOpen={() => setSelectedUser(u)}
            onToggleStatus={() => handleToggleStatus(u)}
          />
        ))}

      {selectedUser && (
        <UserModal
          user={selectedUser}
          roles={roles}
          depots={depots}
          onClose={() => setSelectedUser(null)}
          onSaved={handleSavedFromModal}
          notify={notify}
        />
      )}

      <Toast toast={toast} />

      <style>{`
        @keyframes nwFadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes nwToastIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes nwSpin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

export default UsersPage;
